// Period helpers — uses native Date (no moment dependency).
// Each preset has: key, labelKey, get() → {from, to} as ISO strings (YYYY-MM-DD).
// "total" returns nulls so the backend skips date filtering entirely.

const toIso = (d) => {
  if (!d) return null;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const startOfQuarter = (d) => new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1);
const endOfQuarter = (d) => new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3 + 3, 0);
const startOfYear = (d) => new Date(d.getFullYear(), 0, 1);
const endOfYear = (d) => new Date(d.getFullYear(), 11, 31);

export const PERIOD_PRESETS = [
  {
    key: 'total',
    labelKey: 'Total',
    get: () => ({ from: null, to: null }),
  },
  {
    key: 'thisMonth',
    labelKey: 'This month',
    get: (now = new Date()) => ({
      from: toIso(startOfMonth(now)),
      to: toIso(endOfMonth(now)),
    }),
  },
  {
    key: 'thisQuarter',
    labelKey: 'This quarter',
    get: (now = new Date()) => ({
      from: toIso(startOfQuarter(now)),
      to: toIso(endOfQuarter(now)),
    }),
  },
  {
    key: 'thisYear',
    labelKey: 'This year',
    get: (now = new Date()) => ({
      from: toIso(startOfYear(now)),
      to: toIso(endOfYear(now)),
    }),
  },
  {
    key: 'lastMonth',
    labelKey: 'Last month',
    get: (now = new Date()) => {
      const last = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return { from: toIso(startOfMonth(last)), to: toIso(endOfMonth(last)) };
    },
  },
  {
    key: 'lastQuarter',
    labelKey: 'Last quarter',
    get: (now = new Date()) => {
      const lastQ = new Date(now.getFullYear(), now.getMonth() - 3, 1);
      return { from: toIso(startOfQuarter(lastQ)), to: toIso(endOfQuarter(lastQ)) };
    },
  },
  {
    key: 'lastYear',
    labelKey: 'Last year',
    get: (now = new Date()) => {
      const last = new Date(now.getFullYear() - 1, 0, 1);
      return { from: toIso(startOfYear(last)), to: toIso(endOfYear(last)) };
    },
  },
];

export const getPresetByKey = (key) =>
  PERIOD_PRESETS.find((p) => p.key === key) ?? PERIOD_PRESETS[0];

export const getPeriodRange = (key) => getPresetByKey(key).get();

// For custom ranges we don't match a preset.
export const matchPreset = (from, to) => {
  if (!from && !to) return PERIOD_PRESETS[0];
  for (const preset of PERIOD_PRESETS) {
    if (preset.key === 'total') continue;
    const r = preset.get();
    if (r.from === from && r.to === to) return preset;
  }
  return null;
};

export const PERIOD_KEYS = PERIOD_PRESETS.map((p) => p.key);
